import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Breakpoint } from "@mui/material";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import type { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";

import type {
  BaseFormTemplate,
  CapacityDecisionForContext,
  CreateExternalPatientDemograhicsPullEventRequest,
  CreateForm,
  Form,
  FormContextData,
  FormContextType,
  PatientTimelineResponse,
  SelectedFormsObject,
  ValidationOutcome,
} from "@thalamos/aspire-common";
import {
  baseTemplates,
  canLaunchForm,
  doesUserMeetRequirement,
  FDMCA_TEMPLATE_ID,
  findFormsMerged,
  formIsBid,
  formIsMca,
  h3V1Base,
  initialiseForm,
  isGuestUserSession,
  nonThalamosFormTemplateIds,
  preprocessTemplates,
} from "@thalamos/aspire-common";

import { api } from "../api.js";
import type { FormAssigneeData } from "../components/FormAssignee/FormAssigneeComponent.js";
import { FormAssigneeDialog } from "../components/FormAssignee/FormAssigneeComponent.js";
import { LoggedInUserContext, ManagedLocationsContext } from "../Contexts.js";
import { refusalReason } from "../helpers/classifyApiFailure.js";
import { okResponseData } from "../helpers/okResponseData.js";
import { routeFns } from "../routePaths.js";
import { AdmissionWarningDialog } from "./AdmissionWarningDialog.js";
import { CapacityDecisionDialog } from "./CapacityDecisionDialog.js";
import { CurrentSupportingDocumentsSelector } from "./FormDraft/CurrentSupportingDocumentsSelector.js";
import type { DialogType } from "./FormDraft/FormSupportingDocumentsSelector.js";
import { BannerDialog } from "./FormProgressPage/helpers/BannerDialog.js";
import { ConsiderFdmcaDialog } from "./FormProgressPage/mca/ConsiderFdmcaDialog.js";
import { startLinkedMcaForm } from "./FormProgressPage/mca/startLinkedMcaForm.js";
import {
  useClearSupportingDocuments,
  useFormTemplateUpdater,
} from "./helpers/FormTemplates/FormTemplates.js";
import { PatientTimelineCard } from "./PatientTimelineCard.js";
import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  Typeahead,
} from "~/components/design-system/index.js";

const NEW_FORM_DIALOG_MODES = ["start", "request"] as const;
type NewFormDialogMode = (typeof NEW_FORM_DIALOG_MODES)[number];

// The mode radio group hands back a bare DOM string, so narrow it here rather
// than widening the state it sets.
function isNewFormDialogMode(value: string): value is NewFormDialogMode {
  return (NEW_FORM_DIALOG_MODES as readonly string[]).includes(value);
}

type FormTemplateWithLongDescription = BaseFormTemplate & {
  longDescription: string;
};

export function NewFormDialog({
  patientId,
  closeDialog,
  title,
  allowRequestForm,
  skipMcaStartPrompts,
  existingFormContext,
  formTemplateFilterFn,
  linkedForms: externalLinkedForms,
  allowGuestToLaunchAnyForm,
  existingAdmissions,
  dialogType,
  existingWorkItems,
  maxWidth,
  patientTimeline,
  syncExternalPatientLink,
}: {
  patientId: string;
  closeDialog: () => void;
  title?: string;
  allowRequestForm: boolean;
  skipMcaStartPrompts?: boolean;
  existingFormContext?: FormContextData;
  formTemplateFilterFn: (template: BaseFormTemplate) => boolean;
  linkedForms?: Form<unknown>["linkedForms"];
  allowGuestToLaunchAnyForm?: boolean;
  dialogType?: DialogType;
  existingAdmissions?: Omit<FormContextData, "patient">[];
  existingWorkItems?: Omit<FormContextData, "patient">[];
  maxWidth?: Breakpoint | false;
  patientTimeline?: PatientTimelineResponse;
  syncExternalPatientLink?: (
    reason: CreateExternalPatientDemograhicsPullEventRequest["reason"],
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { category, id: formTemplateIdFromUrl } = useParams();

  // Retrieve details from the user context
  const user = useContext(LoggedInUserContext)!.user;
  const isTrainingContext = user.sessionContext?.isTrainingOrganisation;
  const inTeamContext = user.sessionContext?.type === "team";

  const isAcdEnabled = user.sessionOrganisationConfiguration?.acdEnabled;
  const isMcaEnabled = user.sessionOrganisationConfiguration?.mcaEnabled;
  const isS62Enabled = user.sessionOrganisationConfiguration?.s62Enabled;
  const enabledForms = user.sessionOrganisationConfiguration?.enabledForms;

  // Setup form submission and dialog visibility state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [capacityDecision, setCapacityDecision] = useState<
    CapacityDecisionForContext | undefined
  >(undefined);
  const [showCapacityDecisionDialog, setShowCapacityDecisionDialog] =
    useState(false);

  const [showAdmissionWarningDialog, setShowAdmissionWarningDialog] =
    useState(false);
  const [inviteSuccessMessageName, setInviteSuccessMessageName] = useState();
  const [resolvedInviteName, setResolvedInviteName] = useState<string>();
  const { emailRedirects } = useContext(ManagedLocationsContext);
  const [isOpenBannerDialog, setIsOpenBannerDialog] = useState(false);
  const [isOpenNewFormDialog, setIsOpenNewFormDialog] = useState(true);
  const [showConsiderFdmcaDialog, setShowConsiderFdmcaDialog] = useState(false);

  const hasDoctorWithGmcNumber = user.accreditations.some(
    (accreditation) =>
      accreditation.type === "doctor" && accreditation.gmcNumber,
  );

  // Get all form templates
  const allTemplates = preprocessTemplates(baseTemplates);

  // Form templates where the currently logged-in user can sign the first part of
  // the form, either by virtue of their professional accreditations or their team memberships
  const templateUserMayStartOrInvite = allTemplates.filter((t) => {
    const isCategoryMatch = !category || t.category === category;
    const isAcdMatch = isAcdEnabled || t.id !== "acd";
    const isMcaMatch = isMcaEnabled || t.category !== "mca";
    const isS62Match = isS62Enabled || t.id !== "mha-s62";
    const isGmcNumberMatch = !(hasDoctorWithGmcNumber && t.id === "mha-h3"); // If a doctor has a GMC number then the h3 should not be displayed
    const isFormMatch =
      formTemplateFilterFn(t) &&
      (enabledForms?.some((f) => t.id === f.id && t.version === f.version) ||
        existingFormContext);
    const isThalamosTemplate = !nonThalamosFormTemplateIds.includes(t.id);

    // For forms that define a broader view/invite gate (e.g. S62), restrict
    // the start-or-invite set to users who meet it. Forms without the field
    // are unaffected and behave exactly as before.
    const viewAndInviteRequirement =
      t.parts[0]?.signing.viewAndInviteRequirement;
    const meetsViewAndInviteRequirement =
      !viewAndInviteRequirement ||
      doesUserMeetRequirement(
        {
          ...user,
          memberships: user.sessionContext ? [user.sessionContext] : [],
        },
        viewAndInviteRequirement,
      ) ||
      (allowGuestToLaunchAnyForm && isGuestUserSession(user));

    return (
      isCategoryMatch &&
      isAcdMatch &&
      isMcaMatch &&
      isS62Match &&
      isGmcNumberMatch &&
      isFormMatch &&
      isThalamosTemplate &&
      meetsViewAndInviteRequirement
    );
  });

  const templateUserMayStart = templateUserMayStartOrInvite.filter((t) => {
    return (
      t.parts[0] !== undefined &&
      (doesUserMeetRequirement(
        {
          ...user,
          memberships: user.sessionContext ? [user.sessionContext] : [],
        },
        t.parts[0].signing.userOrTeamRequirement,
      ) ||
        (allowGuestToLaunchAnyForm && isGuestUserSession(user)))
    );
  });

  // `categoryLaunchForStaleUrl` redirects within the `patientFormLaunch` route,
  // so this dialog stays mounted with the URL's form still selected: open in a
  // mode that offers it.
  const initialMode: NewFormDialogMode =
    !!templateUserMayStart.length &&
    (formTemplateIdFromUrl === undefined ||
      templateUserMayStart.some((t) => t.id === formTemplateIdFromUrl))
      ? "start"
      : "request";
  const [selectedMode, setSelectedMode] =
    useState<NewFormDialogMode>(initialMode);

  const templatesForMode = (mode: NewFormDialogMode) =>
    mode === "start" ? templateUserMayStart : templateUserMayStartOrInvite;
  const activeTemplates = templatesForMode(selectedMode);

  // The form a launch URL names must resolve whether the user may start it or
  // only invite someone to, both to continue it while it is in progress and to
  // carry it through the `categoryLaunchForStaleUrl` redirect.
  const selectableTemplates = formTemplateIdFromUrl
    ? templateUserMayStartOrInvite
    : activeTemplates;

  const { initialFormInputValue, initialFormTemplate } = initialiseForm(
    selectableTemplates,
    formTemplateIdFromUrl,
  );

  const [formTemplate, setFormTemplate] = useState<
    FormTemplateWithLongDescription | undefined
  >(initialFormTemplate);

  const [formInputValue, setFormInputValue] = useState<string>(
    initialFormInputValue,
  );

  // Starting acts on whatever template is selected, so a template the new mode
  // does not offer must not stay selected.
  function changeMode(mode: NewFormDialogMode) {
    if (
      formTemplate !== undefined &&
      !templatesForMode(mode).some((t) => t.id === formTemplate.id)
    ) {
      setFormTemplate(undefined);
      setFormInputValue("");
    }
    setSelectedMode(mode);
  }

  const formTemplateId = formTemplate?.id;
  const isH3Form = formTemplateId === h3V1Base.id;
  const signingRequirements = formTemplate?.parts?.[0]?.signing;
  const linkableForms = signingRequirements?.linkableForms;
  const requiresSupportingForms = !!existingFormContext && !!linkableForms;

  useFormTemplateUpdater(
    formTemplateIdFromUrl,
    formInputValue,
    selectableTemplates,
    formTemplate,
    setFormTemplate,
  );

  const formId = useMemo(() => v4(), []);
  const formDraftId = useMemo(() => v4(), []);
  const assignmentId = useMemo(() => v4(), []);
  const newFormContextId = useMemo(() => v4(), []);

  // Supporting document (e.g. medical recommendations) selection state
  const [supportingFormsProvided, setSupportingFormsProvided] =
    useState<boolean>(false);
  const [selectedForms, setSelectedForms] = useState<SelectedFormsObject[]>([]);
  const [notSelectedReason, setNotSelectedReason] = useState<string>("");
  const [validationOutcome, setValidationOutcome] = useState<
    ValidationOutcome[] | null
  >(null);

  useClearSupportingDocuments(
    formTemplateId,
    setSupportingFormsProvided,
    setSelectedForms,
    setNotSelectedReason,
    setValidationOutcome,
  );

  // Read imperatively so this dialog never shares the page's notification
  // channels: `useChannel` unsubscribes by channel name on cleanup, so an
  // apiHooks read here would kill the page's live updates when the dialog closes.
  const getCurrentFormContext = useCallback(async () => {
    if (existingFormContext?.id === undefined) return null;
    const response = await api.forms.getFormContext(existingFormContext.id);
    return okResponseData(response.data, response) ?? null;
  }, [existingFormContext?.id]);
  const getCurrentPatientTimeline = useCallback(async () => {
    const response = await api.patients.getTimeline(patientId);
    return okResponseData(response.data, response) ?? null;
  }, [patientId]);

  const selectedLinkableForm = formTemplate?.linkableForms?.find((f) =>
    selectedForms.map((s) => s.templateId).includes(f.templateId),
  );

  const linkedForms =
    externalLinkedForms ||
    (requiresSupportingForms &&
      selectedForms.map(({ id }) => ({
        id,
        reason: selectedLinkableForm?.reason || "supported-by",
      }))) ||
    undefined;

  const matchingWorkItems =
    existingWorkItems?.filter((existingWorkItem) => {
      return existingWorkItem.forms.some((form) => {
        return form.formTemplate.id === formTemplate?.id;
      });
    }) || [];

  // A launch URL outlives the work it was for: a reload, Back or a bookmark can
  // reach it once that work is finished or discarded elsewhere.
  const categoryLaunchForStaleUrl =
    formTemplateIdFromUrl !== undefined &&
    formTemplate?.id === formTemplateIdFromUrl &&
    !matchingWorkItems.length
      ? routeFns.patientFormLaunch(patientId, formTemplate.category)
      : undefined;

  useEffect(() => {
    if (categoryLaunchForStaleUrl !== undefined) {
      navigate(categoryLaunchForStaleUrl, { replace: true });
    }
  }, [categoryLaunchForStaleUrl, navigate]);

  const canContinueOrStartForm = canLaunchForm({
    linkableForms,
    supportingFormsProvided,
    notSelectedReason,
    validationOutcome,
    requiresSupportingForms,
  });

  // MCA forms are held in their own context type from the outset, so that a
  // partner MCA form started later can join the same assessment.
  const newFormContextType: FormContextType = formTemplate
    ? formIsMca(formTemplate)
      ? "mca-assessment"
      : "standalone"
    : "standalone";
  // Skipping the MCA start prompts still needs a placeholder decision because
  // MCA contexts cannot be created without one.
  const defaultCapacityDecision =
    skipMcaStartPrompts && newFormContextType === "mca-assessment"
      ? { type: "other" as const, description: "N/A" }
      : undefined;

  async function createForm(
    workItem: CreateForm & { id: string },
    decisionOverride?: CapacityDecisionForContext,
  ): Promise<AxiosResponse<unknown, unknown> | undefined> {
    if (formTemplate === undefined) {
      throw new Error("Cannot create a form before its template is chosen");
    }
    // Resolve a rejection, such as a 5xx or no response, so callers report it
    // as they do a refusal.
    return api.forms
      .create(formId, {
        patientId,
        formTemplate: { id: formTemplate.id, version: formTemplate.version },
        workItem: existingFormContext ? undefined : workItem,
        formContext: existingFormContext
          ? { mode: "existing", id: existingFormContext.id }
          : {
              mode: "new",
              type: newFormContextType,
              id: newFormContextId,
              capacityDecision:
                decisionOverride ?? capacityDecision ?? defaultCapacityDecision,
            },
        linkedForms,
      })
      .catch(() => undefined);
  }

  // Starting can be confirmed from dialogs that do not show `submitError`, so a
  // refusal is toasted instead.
  const showStartRefused = (response: AxiosResponse<unknown> | undefined) => {
    renderErrorToast({
      message:
        (response && refusalReason(response)) ?? t("errors.unknownError"),
    });
  };

  const startCreatedForm = async () => {
    const result = await api.drafts
      .create(formDraftId, { formId })
      .catch(() => undefined);

    if (result?.status === 201) {
      navigate(routeFns.formDraftsComplete(formDraftId, patientId), {
        replace: true,
      });
    } else {
      showStartRefused(result);
    }
  };

  const needsCapacityDecision =
    !existingFormContext &&
    newFormContextType === "mca-assessment" &&
    !defaultCapacityDecision;

  // A Best Interest Decision started without an existing context offers the
  // FDMCA interstitial before the new draft opens.
  const offerFdmcaBeforeBid =
    !existingFormContext && !!formTemplate && formIsBid(formTemplate);

  const launchNewFormFn = async (
    decisionOverride?: CapacityDecisionForContext,
  ) => {
    // Sync with EPR before starting a new form
    if (syncExternalPatientLink) {
      await syncExternalPatientLink("started form");
    }

    setSubmitError(undefined);
    const workItem = {
      id: assignmentId,
      assignedUserId: user.id,
      teamId: (inTeamContext ? user.sessionContext!.teamId : undefined)!,
    };

    const createAssignmentResult = await createForm(workItem, decisionOverride);

    if (createAssignmentResult?.status === 204) {
      if (offerFdmcaBeforeBid) {
        setIsOpenNewFormDialog(false);
        setShowConsiderFdmcaDialog(true);
        return;
      }

      await startCreatedForm();

      return;
    } else {
      showStartRefused(createAssignmentResult);
    }
  };

  const requestForm = async (values: FormAssigneeData) => {
    setSubmitError(undefined);
    setSubmitting(true);

    const redirect = emailRedirects.find(
      (er) => er.sourceEmail.toLowerCase() === values.email.toLowerCase(),
    );
    const displayName = redirect
      ? (redirect.targetTeamName ?? inviteSuccessMessageName)
      : inviteSuccessMessageName;

    const workItem =
      values.type === "guest"
        ? { id: assignmentId, email: values.email, name: values.name }
        : {
            id: assignmentId,
            teamId: values.teamId!,
            assignedUserId: values.assignedUserId,
          };
    const result = await createForm(workItem);

    if (result?.status === 204) {
      setResolvedInviteName(displayName);
      setIsOpenNewFormDialog(false);
      setIsOpenBannerDialog(true);
      renderSuccessToast({
        message: `Request successfully sent to ${displayName}`,
      });
    } else {
      setSubmitError(
        // @ts-expect-error - data is "unknown"
        result?.data?.reason || "Unknown error",
      );
    }
    setSubmitting(false);
  };

  const saveFn = async () => {
    if (isH3Form && !!existingAdmissions?.length)
      return setShowAdmissionWarningDialog(true);
    // A new MCA assessment has to name its decision before it exists: the decision is
    // what couples a signed assessment to the resident's status, so a context created
    // without one can never move it.
    if (needsCapacityDecision && !capacityDecision)
      return setShowCapacityDecisionDialog(true);
    if (!!matchingWorkItems.length) {
      return navigate(
        routeFns.patientFormLaunch(
          patientId!,
          formTemplate!.category,
          formTemplateId,
        ),
      );
    }
    setSubmitting(true);
    try {
      await launchNewFormFn();
    } finally {
      setSubmitting(false);
    }
  };

  const completeDialog = (primaryButton: ReactNode) => (
    <>
      {submitError && (
        <Box sx={{ my: 2 }}>
          <Banner title={submitError} bannerType={BannerList.ERROR} />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Button
          testId="close-modal-button"
          variant="outlined"
          label={t("pages.patientHome.launchForm.close")}
          onClick={closeDialog}
        />
        {primaryButton}
      </Box>
    </>
  );

  let content;

  if (showCapacityDecisionDialog) {
    return (
      <CapacityDecisionDialog
        patientId={patientId}
        onCancel={() => {
          setShowCapacityDecisionDialog(false);
        }}
        onConfirm={(decision) => {
          setCapacityDecision(decision);
          setShowCapacityDecisionDialog(false);
          void (async () => {
            setSubmitting(true);
            await launchNewFormFn(decision);
            setSubmitting(false);
          })();
        }}
      />
    );
  }

  if (showAdmissionWarningDialog) {
    content = (
      <AdmissionWarningDialog
        existingAdmissions={existingAdmissions!}
        patientId={patientId}
        launchNewFormFn={launchNewFormFn!}
        patientTimeline={patientTimeline}
        syncExternalPatientLink={syncExternalPatientLink}
      />
    );
  } else if (formTemplateIdFromUrl && categoryLaunchForStaleUrl === undefined) {
    // A URL can name any template, including one this user may neither start
    // nor invite someone to start here.
    content =
      formTemplate?.id === formTemplateIdFromUrl ? (
        <FormAlreadyInProgressDialog
          closeDialog={closeDialog}
          launchNewFormFn={
            templateUserMayStart.some((t) => t.id === formTemplate.id)
              ? launchNewFormFn
              : undefined
          }
          patientId={patientId}
          description={formTemplate.longDescription}
          matchingWorkItems={matchingWorkItems}
          patientTimeline={patientTimeline}
        />
      ) : (
        <>
          <PopupDialogTitle titleText={title} closeDialog={closeDialog} />
          <Box sx={{ mb: 4 }}>
            <Banner
              title={t("pages.patientHome.launchForm.formUnavailable")}
              bannerType={BannerList.ERROR}
            />
          </Box>
          {completeDialog(null)}
        </>
      );
  } else {
    content = (
      <>
        <PopupDialogTitle titleText={title} closeDialog={closeDialog} />
        <Box>
          {/* If requesting a form is allowed and there ARE NO form types that the user is allowed 
            to create then show a message saying to "invite another user" */}
          {allowRequestForm && !templateUserMayStart.length && (
            <Box sx={{ mb: 4 }}>
              <Banner
                title={t("pages.patientHome.launchForm.requestFormCompletion")}
              />
            </Box>
          )}

          {/* If requesting a form is allowed and there ARE SOME form types that the user is allowed to create
              Then allow the user to pick between "starting the form" themselves or "inviting another user" */}
          {allowRequestForm && !!templateUserMayStart.length && (
            <>
              <FormModeSelection
                selectedMode={selectedMode}
                onChangeMode={changeMode}
                formTemplateId={formTemplateId}
              />
            </>
          )}

          {/* Form template input */}
          <Box sx={{ mb: 0.5 }}>
            <Typeahead
              testId="select-new-form"
              onInputChange={setFormInputValue}
              placeholder={t("components.typeahead.placeholder")}
              name={"select-form"}
              inputValue={formInputValue}
              label={t("components.typeahead.label")}
              options={activeTemplates.map((f) => ({
                value: f.id,
                label: f.longDescription,
              }))}
            />
          </Box>

          {/* If we are in "start" mode (or the user has not yet selected a form template) 
              Then just show the footer buttons */}
          {(selectedMode === "start" || !formTemplate) &&
            !formTemplateIdFromUrl && (
              <>
                {requiresSupportingForms ? (
                  <>
                    <CurrentSupportingDocumentsSelector
                      getCurrentFormContext={getCurrentFormContext}
                      getCurrentPatientTimeline={getCurrentPatientTimeline}
                      patientTimeline={patientTimeline}
                      formContext={existingFormContext}
                      linkableForms={linkableForms}
                      selectedForms={selectedForms}
                      setSelectedForms={setSelectedForms}
                      notSelectedReason={notSelectedReason}
                      setNotSelectedReason={setNotSelectedReason}
                      supportingFormsProvided={supportingFormsProvided}
                      dialogType={dialogType}
                      setSupportingFormsProvided={setSupportingFormsProvided}
                      setValidationOutcome={setValidationOutcome}
                    />
                    {completeDialog(
                      <Button
                        testId="start-form-button"
                        label={t("pages.patientHome.launchForm.startForm")}
                        disabled={submitting || !canContinueOrStartForm}
                        onClick={() => {
                          void saveFn();
                        }}
                      />,
                    )}
                  </>
                ) : (
                  <>
                    {completeDialog(
                      <Button
                        testId="start-form-button"
                        label={t("pages.patientHome.launchForm.startForm")}
                        disabled={
                          submitting ||
                          !formTemplate ||
                          selectedMode === "request"
                        }
                        onClick={() => {
                          void saveFn();
                        }}
                      />,
                    )}
                  </>
                )}
              </>
            )}

          {/* If we are in "requst" mode (and the user has selected a form template)
              Then show the "select assignee" control AND the footer buttons */}
          {selectedMode === "request" && formTemplate && (
            <>
              <FormAssigneeDialog
                formDetails={{
                  template: {
                    id: formTemplate.id,
                    version: formTemplate.version,
                  },
                  part: 1,
                }}
                user={user}
                setInviteSuccessMessageName={setInviteSuccessMessageName}
                onSave={requestForm}
                submissionComponentFn={(formikValues) => {
                  const {
                    type: userType,
                    isTrainingOrganisation,
                    name,
                    teamId,
                  } = formikValues.values;

                  const userNotSelected =
                    userType === "full-user" ? !teamId : !name;

                  const shareToNonTrainingUserFromTraining =
                    !userNotSelected &&
                    isTrainingContext &&
                    !isTrainingOrganisation;

                  const shareToTrainingUserFromNonTraining =
                    !userNotSelected &&
                    !isTrainingContext &&
                    isTrainingOrganisation;

                  let warningMessage = null;
                  if (shareToNonTrainingUserFromTraining)
                    warningMessage = t("training.onlyShareToTrainingUsers");
                  if (shareToTrainingUserFromNonTraining)
                    warningMessage = t("training.cannotShareToTrainingUsers");

                  return (
                    <>
                      {warningMessage && (
                        <Box sx={{ mb: 4 }}>
                          <Banner
                            title={warningMessage}
                            bannerType={BannerList.WARNING}
                          />
                        </Box>
                      )}
                      {completeDialog(
                        <Button
                          testId="invite-user-button"
                          label={t("pages.patientHome.launchForm.inviteUser")}
                          disabled={
                            submitting ||
                            userNotSelected ||
                            !!shareToNonTrainingUserFromTraining ||
                            !!shareToTrainingUserFromNonTraining
                          }
                          onClick={formikValues.submitForm}
                        />,
                      )}
                    </>
                  );
                }}
              />
            </>
          )}
        </Box>
      </>
    );
  }
  return (
    <>
      <PopupDialog
        open={isOpenNewFormDialog}
        fullWidth
        maxWidth={maxWidth}
        testId="new-form-modal"
      >
        {content}
      </PopupDialog>
      {showConsiderFdmcaDialog && (
        <ConsiderFdmcaDialog
          onContinueToBid={startCreatedForm}
          onStartFdmca={async () => {
            const fdmcaStart = await startLinkedMcaForm({
              formContextId: newFormContextId,
              patientId,
              formTemplateId: FDMCA_TEMPLATE_ID,
            });

            if (!fdmcaStart.ok) {
              renderErrorToast({
                message:
                  fdmcaStart.refusal ??
                  t("pages.mcaAssessment.failedCreateForm"),
              });
              return;
            }

            navigate(
              routeFns.formDraftsComplete(fdmcaStart.formDraftId, patientId),
              { replace: true },
            );
          }}
        />
      )}
      {isOpenBannerDialog && (
        <BannerDialog
          title={t("pages.patientHome.launchForm.inviteSuccessfullySentLabel")}
          message={`
            ${t(
              "pages.patientHome.launchForm.inviteSuccessfullySentMessage",
            )} ${resolvedInviteName ?? inviteSuccessMessageName}`}
          onClose={() => {
            navigate(routeFns.patientHome(patientId!));
          }}
        />
      )}
    </>
  );
}

function FormAlreadyInProgressDialog({
  closeDialog,
  matchingWorkItems,
  description,
  launchNewFormFn,
  patientId,
  patientTimeline,
}: {
  closeDialog: () => void;
  matchingWorkItems: Omit<FormContextData, "patient">[];
  description: string;
  /** Omitted when the user may not start this form, only continue it. */
  launchNewFormFn?: () => Promise<void>;
  patientId: string;
  patientTimeline?: PatientTimelineResponse;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedRadioValue, setSelectedRadioValue] = useState(null);
  const [continueWarningMessage, setContinueWarningMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <>
      <PopupDialogTitle
        titleText={t("pages.patientHome.launchForm.popupDialogTitle")}
        closeDialog={closeDialog}
      />
      <Box sx={{ mb: 4 }}>
        <Banner
          title={
            `${description} ` +
            t(`pages.patientHome.launchForm.formProgressWarningMessage`)
          }
          bannerType={BannerList.WARNING}
        />
        {!!matchingWorkItems.length && (
          <Box sx={{ mt: 4 }}>
            {continueWarningMessage && !selectedRadioValue && (
              <Typography sx={{ color: "red", mb: 1 }}>
                {continueWarningMessage}
              </Typography>
            )}
            {matchingWorkItems.map((matchingWorkItem) => {
              const merged = findFormsMerged(
                patientTimeline?.mergedPatientData,
                matchingWorkItem,
              );
              return (
                <PatientTimelineCard
                  key={matchingWorkItem.id}
                  formContext={matchingWorkItem}
                  showRadioSelector={matchingWorkItems.length > 1}
                  selectedRadioValue={selectedRadioValue!}
                  setSelectedRadioValue={setSelectedRadioValue}
                  lastMerged={merged?.lastMerged}
                />
              );
            })}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent:
            launchNewFormFn === undefined ? "flex-end" : "space-between",
        }}
      >
        {launchNewFormFn !== undefined && (
          <Button
            variant="outlined"
            label={t("buttonLabels.startNew")}
            testId="start-new-button"
            disabled={isSubmitting}
            onClick={() => {
              void (async () => {
                setIsSubmitting(true);
                try {
                  await launchNewFormFn();
                } finally {
                  setIsSubmitting(false);
                }
              })();
            }}
          />
        )}
        <Button
          label={t("buttonLabels.continue")}
          testId="continue-button"
          disabled={isSubmitting}
          onClick={() => {
            if (selectedRadioValue) {
              navigate(routeFns.formContextPage(selectedRadioValue, patientId));
              return;
            }

            const onlyWorkItem =
              matchingWorkItems.length === 1 ? matchingWorkItems[0] : undefined;
            if (onlyWorkItem !== undefined) {
              navigate(routeFns.formContextPage(onlyWorkItem.id, patientId));
              return;
            }
            setContinueWarningMessage(
              t("pages.patientHome.launchForm.radioSelectorWarningMessage"),
            );
          }}
        />
      </Box>
    </>
  );
}

function FormModeSelection({
  selectedMode,
  onChangeMode,
  formTemplateId,
}: {
  selectedMode: NewFormDialogMode;
  onChangeMode: (mode: NewFormDialogMode) => void;
  formTemplateId: string | undefined;
}) {
  const { t } = useTranslation();
  return (
    <FormControl>
      <RadioGroup
        aria-labelledby="form-mode-selection-label"
        value={selectedMode}
        onChange={(e) => {
          if (isNewFormDialogMode(e.target.value)) {
            onChangeMode(e.target.value);
          }
        }}
        name="form-mode-selection"
      >
        <FormControlLabel
          value="start"
          control={<Radio />}
          label={t("pages.patientHome.launchForm.startNew")}
        />
        <FormControlLabel
          value="request"
          control={<Radio />}
          label={t("pages.patientHome.launchForm.requestFormCompletion")}
        />
      </RadioGroup>
      {selectedMode === "start" && formTemplateId === "mha-h3" && (
        <ValidityBanner />
      )}
    </FormControl>
  );
}

function ValidityBanner() {
  const { t } = useTranslation();
  return (
    <Banner
      title={t("pages.patientHome.launchForm.admissionValidityInstructions")}
      bannerType={BannerList.WARNING}
    />
  );
}
